<template>
	<div id="footerSchedule" class="schedule">
		<Collapse ref="scheduleCollapse" :opened="collapseOpened">
			<template #collapse-header>
				<h3 class="schedule-title">
					Operating Hours
				</h3>
			</template>
			<template #collapse-body>
				<hours-switcher
					menu-style
					:switcher-labels="filteredHoursTypes"
					:active-tab="scheduleType"
					@handle-change="switchScheduleType"
				/>
				<div class="schedule-content">
					<ul class="schedule-list">
						<li
							v-for="(day, i) in weekDays"
							class="schedule-list-item"
							:class="{ active: getIsActive(i) }"
							:key="i"
						>
							<span class="schedule-list-day">{{ day.long }}</span>
							<ul class="schedule-time">
								<template v-if="!getHours(i).length">
									<li>
										{{ scheduleType === 'Open' ? 'Closed' : 'Non Accessible' }}
									</li>
								</template>
								<template v-else>
									<li v-for="(hours, y) in getHours(i)" :key="y">
										{{ callGetAmPmTime(hours.startTime) }}
										-
										{{ callGetAmPmTime(hours.endTime) }}
									</li>
								</template>
							</ul>
						</li>
					</ul>
					<div class="schedule-notes" v-html="currentNote" />
				</div>
			</template>
		</Collapse>
	</div>
</template>

<script>
import { mapState } from 'vuex'
// Utils
import { getAmPmTime, getCleanHTML, getDateNow } from '@/utils/helpers'
// Components
import HoursSwitcher from '@/components/HoursSwitcher'
import Collapse from '@/components/Coollapse.vue'
// Constants
import { TIME_FORMATS, WEEK_DAYS } from '@/constants'

const SCHEDULE_TYPES = {
	open: 'Open',
	forHere: 'Dine In',
	pickup: 'Pickup'
}

const SCHEDULE_NOTES = {
	Open: 'noteOpenHours',
	'Dine In': 'noteForHereHours',
	Pickup: 'notePickUpHours'
}

const newHoursTypes = Object.values(SCHEDULE_TYPES)

export default {
	name: 'Schedule',

	components: {
		Collapse,
		HoursSwitcher
	},

	props: {
		needHighlightActive: {
			type: Boolean,
			default: false
		},

		collapseOpened: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			scheduleType: 'Open'
		}
	},

	mounted() {
		this.setCurrentHoursType()
	},

	computed: {
		...mapState('venue', ['venueInfo']),

		currentNote() {
			const currentNoteKey = SCHEDULE_NOTES[this.scheduleType]
			const noteHTML = currentNoteKey
				? this.venueInfo.settings[currentNoteKey]
				: ''

			return getCleanHTML(noteHTML)
		},

		filteredHoursTypes() {
			const types = [...newHoursTypes]

			if (!this.venueInfo.acceptForHere) {
				const forHereIndex = types.findIndex(
					type => type === SCHEDULE_TYPES.forHere
				)
				types.splice(forHereIndex, 1)
			}

			if (!this.venueInfo.acceptPickUp) {
				const pickupIndex = types.findIndex(
					type => type === SCHEDULE_TYPES.pickup
				)
				types.splice(pickupIndex, 1)
			}

			this.switchScheduleType(types[0])

			return types
		},

		weekDays() {
			return WEEK_DAYS
		},

		activeDayIndex() {
			return getDateNow(this.venueInfo.timeZone).day()
		},

		schedule() {
			const venueInfo = this.venueInfo

			switch (this.scheduleType) {
				case SCHEDULE_TYPES.toGo:
					return venueInfo.toGoHours
				case SCHEDULE_TYPES.forHere:
					return venueInfo.forHereHours
				case SCHEDULE_TYPES.pickup:
					return venueInfo.pickupHours
				default:
					return venueInfo.openHours
			}
		}
	},

	methods: {
		setCurrentHoursType() {
			const { selected } = this.$route.query

			if (selected && this.isCurrentTypeAccepted(selected)) {
				this.scheduleType = selected
			}
		},

		getIsActive(index) {
			return this.needHighlightActive && index === this.activeDayIndex
		},

		isCurrentTypeAccepted(selected) {
			switch (selected) {
				case SCHEDULE_TYPES.forHere:
					return this.venueInfo.acceptForHere
				case SCHEDULE_TYPES.pickup:
					return this.venueInfo.acceptPickUp
				default:
					return false
			}
		},

		switchScheduleType(val) {
			this.scheduleType = val
		},

		callGetAmPmTime(time) {
			return getAmPmTime(time, TIME_FORMATS.AMPM)
		},

		getHours(dayIndex) {
			return (
				(this.schedule &&
					this.schedule.filter(
						item => item.dow === dayIndex + 1 && !item.closed
					)) ||
				[]
			)
		}
	}
}
</script>

<style lang="scss" scoped>
@import './src/assets/styles/variables';

.schedule {
	width: 100%;
	color: $white;
}

.schedule-title {
	margin: 0 0 24px 0;
	font-size: 16px;
	font-weight: 700;
}

.schedule-content {
	min-height: auto;
	display: flex;
	flex-direction: column;
	max-width: 70%;
}

.schedule-notes {
	word-wrap: break-word;
	padding: 14px 0;
	::v-deep p {
		margin: 0;
	}
}

.schedule-list {
	margin-top: 12px;
	padding: 0;
	color: $white;
	list-style: none;
}

.schedule-list-item {
	position: relative;
	padding: 12px 0;
	overflow: hidden;
	font-size: 16px;
	letter-spacing: 0;
	border-bottom: 1px solid rgba($white, 0.2);

	&.active {
		width: calc(100% + 32px);
		padding-left: 16px;
		padding-right: 16px;
		margin-left: -16px;
		background: $blue-faceless-void;
	}

	&:last-child {
		border-bottom: none;
	}
}

.schedule-list-day,
.schedule-time {
	font-size: 16px;
	letter-spacing: 0;
	color: $white;
}

.schedule-list-day {
	float: left;
}

.schedule-time {
	float: right;
	margin: 0;
	padding: 2px 0 0;
	list-style: none;
	text-align: right;

	li {
		padding: 0 0 4px;

		&:last-child {
			padding: 0;
		}
	}
}

@media (max-width: 767px) {
	.schedule-list {
		padding: 0 8px;
	}

	.schedule-content {
		max-width: 100%;
		min-height: auto;
		margin-bottom: 36px;
	}
}

@media (max-width: 480px) {
	.schedule-content {
		margin-bottom: 0;
	}
}
</style>
